// M13.7 — Insights model registry (SKE-3439).
//
// `/api/insights` always serves the first entry (`DEFAULT_INSIGHTS_MODEL_ID`).
// The wider list exists to feed the offline bench harness in `scripts/bench-insights.ts`
// — every entry is a candidate the bench can rotate through. The dev model dropdown
// previously fed by this list has been removed; production behaviour is unchanged.
//
// Slugs are verified against `https://openrouter.ai/api/v1/models` —
// check that endpoint before adding or renaming anything here.

export interface InsightsModelOption {
  /** OpenRouter slug. Must match the `model` field on the
   *  /chat/completions request verbatim. */
  id: string;
  /** Short human label shown in the dropdown. */
  label: string;
  /** Single-phrase role this model plays in the comparison harness. */
  role: string;
}

export const INSIGHTS_MODELS: readonly InsightsModelOption[] = [
  // Rotation history (see docs/insights-model-bench.md for the eval run):
  // - 2026-04-21 round 1: dropped gemini-3.1-pro-preview, qwen3.5-flash-02-23,
  //   kimi-k2.5 (all 0/4). Added grok-4.1-fast, deepseek-v3.2, glm-4.7-flash.
  // - 2026-04-21 round 2: grok-4.1-fast scored perfect 5.00 quality at
  //   924ms and 0.145¢ — moved to first entry + made prod default in
  //   src/server/openrouter/client.ts::OPENROUTER_DEFAULTS. Dropped
  //   glm-4.7-flash (0/4 again). Added gpt-5.4-nano as a cheap OpenAI
  //   fast-tier alternative to round out the dropdown to 10.
  // - 2026-04-22 SKE-3468 round 1: added 6 analytical/math-strong
  //   reasoning models (o3, o4-mini-high, claude-opus-4.7, r1-0528,
  //   qwen3-max-thinking, grok-4) for side-by-side comparison.
  // - 2026-04-22 SKE-3468 round 2: **speculatively promoted
  //   `deepseek/deepseek-r1-0528` to prod default** targeting stronger
  //   reasoning on contextual recommendations. Shipped UNBENCHED.
  // - 2026-04-22 SKE-3470 (v9 bench): r1-0528 scored **0/4** — every
  //   fixture timed out at the 60s total budget with parse_failure.
  //   Reasoning-token streams never produced valid JSON inside budget.
  //   **Reverted to `x-ai/grok-4.1-fast` as the known-good default.**
  //   `meta-llama/llama-4-maverick` is the new v9 star (4/4 passes,
  //   0.086¢/call — 45% cheaper than grok — NA 4.75, the highest in
  //   the shortlist); promote after a second bench cycle confirms it
  //   holds up. r1-0528 kept in the dropdown marked as "failed v9".
  {
    id: "x-ai/grok-4.3",
    label: "xAI Grok 4.3",
    role: "default; replaces retired grok-4.1-fast (2026-06-04), rebench pending",
  },
  {
    id: "meta-llama/llama-4-maverick",
    label: "Meta Llama 4 Maverick",
    role: "v9 star candidate — 4/4, 0.086¢/call, NA 4.75 (highest)",
  },
  {
    id: "openai/gpt-5.4",
    label: "OpenAI GPT-5.4",
    role: "top overall quality on bench (4.55)",
  },
  {
    id: "openai/gpt-5.4-mini",
    label: "OpenAI GPT-5.4 Mini",
    role: "OpenAI fast tier (mid-priced)",
  },
  {
    id: "openai/gpt-5.4-nano",
    label: "OpenAI GPT-5.4 Nano",
    role: "cheapest OpenAI tier",
  },
  {
    id: "anthropic/claude-sonnet-4.6",
    label: "Anthropic Claude Sonnet 4.6",
    role: "reasoning + instruction following",
  },
  {
    id: "anthropic/claude-haiku-4.5",
    label: "Anthropic Claude Haiku 4.5",
    role: "fast Anthropic + final safety net on FALLBACK_CHAIN",
  },
  {
    id: "google/gemini-3-flash-preview",
    label: "Google Gemini 3 Flash",
    role: "fast Google",
  },
  {
    id: "nvidia/nemotron-3-super-120b-a12b",
    label: "NVIDIA Nemotron 3 Super 120B",
    role: "open-weights reasoning",
  },
  {
    id: "mistralai/mistral-small-2603",
    label: "Mistral Small 4 (26-03)",
    role: "cheapest reliable tier (quality slightly below top)",
  },
  {
    id: "deepseek/deepseek-v3.2",
    label: "DeepSeek V3.2",
    role: "strong Chinese-lab open-weights alt (flaky reliability)",
  },
  // Reasoning tier — slower + pricier, but target analytical/math
  // rigour on contextual recommendations. Added 2026-04-22 (SKE-3468).
  {
    id: "openai/o3",
    label: "OpenAI o3",
    role: "reasoning tier — SOTA math, science, analytical",
  },
  {
    id: "openai/o3-pro",
    label: "OpenAI o3-pro",
    role: "reasoning tier — premium o3 (expensive, longest thinking)",
  },
  {
    id: "openai/o4-mini-high",
    label: "OpenAI o4-mini (high effort)",
    role: "reasoning tier — cheap OpenAI reasoning at max thinking",
  },
  {
    id: "anthropic/claude-opus-4.7",
    label: "Anthropic Claude Opus 4.7",
    role: "reasoning tier — top Anthropic (used as bench judge)",
  },
  {
    id: "google/gemini-3.1-pro-preview",
    label: "Google Gemini 3.1 Pro",
    role: "reasoning tier — Google frontier reasoning",
  },
  {
    id: "qwen/qwen3-max-thinking",
    label: "Qwen3-Max Thinking",
    role: "reasoning tier — Qwen flagship reasoning, open-weights alt",
  },
  {
    id: "x-ai/grok-4",
    label: "xAI Grok 4",
    role: "reasoning tier — full Grok-4 with parallel tool calling",
  },
  {
    id: "x-ai/grok-4.20",
    label: "xAI Grok 4.20",
    role: "reasoning tier — xAI flagship (used as bench judge)",
  },
  {
    id: "deepseek/deepseek-v3.2-speciale",
    label: "DeepSeek V3.2 Speciale",
    role: "reasoning tier — DeepSeek max-compute (0/4 v9, parse_failure at budget)",
  },
  {
    id: "deepseek/deepseek-r1-0528",
    label: "DeepSeek R1 (05-28)",
    role: "reasoning tier — math-heavy (0/4 v9, parse_failure at budget; was prod default briefly)",
  },
] as const;

export const DEFAULT_INSIGHTS_MODEL_ID = INSIGHTS_MODELS[0].id;

/**
 * Default load / silent-fallback chain for `/api/insights`. Ordered by
 * (pass-rate, cost, latency) from docs/insights-model-bench.md. When
 * the primary model errors with transport / HTTP 5xx / repeated parse
 * failures, the generator silently advances to the next entry. End
 * users see a single skeleton through the whole chain; telemetry
 * surfaces which hops ran via `telemetry.fallbackChain`.
 *
 * Depth matters: each hop adds up to ~25s to the perceived latency.
 * Three entries lets us exhaust within the 60s total budget.
 */
export const FALLBACK_CHAIN = [
  // 2026-04-22 SKE-3470 — reverted to the pre-SKE-3468 chain after v9
  // bench showed r1-0528 failing 0/4 with parse_failure at the 60s
  // budget ceiling on every fixture. Primary + fallback + safety net
  // are all 4/4 passing models with proven reliability; no reasoning-
  // tier hop in the chain until a reasoning model demonstrates it
  // can hit the 60s budget with valid JSON on our prompt.
  "x-ai/grok-4.3", // primary; replaces retired grok-4.1-fast (2026-06-04)
  "openai/gpt-5.4-nano", // fallback — 4/4 v9, 0.199¢/call, 478ms avg (different provider — diversity)
  "anthropic/claude-haiku-4.5", // safety net — 4/4 v8+v9, 0.986¢/call, highest reliability
] as const;

/**
 * Explicit-retry escalation chain used when the user clicks Regenerate
 * *from inside the error panel* (i.e. after seeing a failure). Swaps
 * the cheap default tier for the mid- and top-reasoning models because
 * a deliberate retry signals "I want a better answer, not the same one
 * faster". Call cost rises ~6–15× relative to FALLBACK_CHAIN; the
 * dedicated `INSIGHTS_ESCALATE` rate-limit bucket (5/hr/(user, property))
 * caps the blast radius.
 */
export const ESCALATION_CHAIN = [
  "openai/gpt-5.4-mini", // mid-reasoning — 4/4, 1.00¢, 881ms
  "openai/gpt-5.4", // top overall — 4/4, 2.46¢, 684ms, OU 4.67
  "anthropic/claude-haiku-4.5", // safety net — 4/4, 0.95¢, 2040ms
] as const;

export type InsightsChain = readonly string[];

// SKE-3453 — Per-model latency percentiles sourced from
// `docs/insights-model-bench.csv`. Regenerate with `pnpm bench:insights:latency`
// after any fresh bench run. Read by the client `InsightsSkeleton` to
// size the progress bar (fill to 90% of p95) and decide when to show
// the "running long" hint (elapsed > p95 × 1.5).
//
// Do NOT hand-edit between the anchor comments below — the script
// rewrites that region verbatim. Any hand-edits will be lost on the
// next `pnpm bench:insights:latency`.

// BEGIN MODEL_LATENCY_STATS (generated — update via pnpm bench:insights:latency)
export const MODEL_LATENCY_STATS: Readonly<
  Record<string, { p50: number; p95: number; avgMs: number }>
> = {
  "anthropic/claude-haiku-4.5": { p50: 983, p95: 1139, avgMs: 997 }, // 4 samples
  "anthropic/claude-opus-4.7": { p50: 1733, p95: 2468, avgMs: 1732 }, // 4 samples
  "anthropic/claude-sonnet-4.6": { p50: 4895, p95: 6474, avgMs: 4230 }, // 3 samples
  "deepseek/deepseek-v3.2": { p50: 4005, p95: 4005, avgMs: 4005 }, // 1 samples
  "google/gemini-3-flash-preview": { p50: 2037, p95: 2157, avgMs: 2028 }, // 4 samples
  "meta-llama/llama-4-maverick": { p50: 637, p95: 992, avgMs: 638 }, // 3 samples
  "mistralai/mistral-small-2603": { p50: 726, p95: 1168, avgMs: 816 }, // 4 samples
  "openai/gpt-5.4": { p50: 666, p95: 1219, avgMs: 762 }, // 4 samples
  "openai/gpt-5.4-mini": { p50: 521, p95: 1246, avgMs: 773 }, // 3 samples
  "openai/gpt-5.4-nano": { p50: 571, p95: 675, avgMs: 571 }, // 4 samples
  "openai/o3": { p50: 610, p95: 916, avgMs: 651 }, // 4 samples
  "qwen/qwen3-max-thinking": { p50: 1640, p95: 2232, avgMs: 1640 }, // 2 samples
  "x-ai/grok-4.1-fast": { p50: 550, p95: 1319, avgMs: 758 }, // 4 samples
  "x-ai/grok-4.20": { p50: 525, p95: 744, avgMs: 577 }, // 4 samples
};
// END MODEL_LATENCY_STATS

/** Conservative default when a model slug is absent from
 *  `MODEL_LATENCY_STATS` (a new entry shipped before the next bench
 *  run). 2s p50 / 6s p95 keeps the progress bar honest without
 *  overclaiming speed for an unmeasured model. */
export const DEFAULT_LATENCY_STATS = { p50: 2000, p95: 6000, avgMs: 2500 } as const;

/** Resolve latency stats for a model slug, falling back to
 *  `DEFAULT_LATENCY_STATS` when the bench hasn't sampled the model. */
export function getModelLatencyStats(modelId: string): {
  p50: number;
  p95: number;
  avgMs: number;
} {
  return MODEL_LATENCY_STATS[modelId] ?? DEFAULT_LATENCY_STATS;
}
